import React, { useEffect, useRef, useState } from "react";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";
import * as String from "../commons/string";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { useAlert } from "../provider/AlertProvider";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { Button, Col, Collapse, Divider, Input, InputRef, Modal, Space } from "antd";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { SearchOutlined } from "@ant-design/icons";

import Highlighter from "react-highlight-words";
import DataTable from "../components/DataTable";

const { Panel } = Collapse;

type DataIndex = keyof Types.EventUserDto;

function EventUserList({ eventId }: { eventId: number }) {
    const alert = useAlert();

    const [eventUsers, setEventUsers] = useState<Array<Types.EventUserDto>>([]);
    const [eventPrizeStats, setEventPrizeStats] = useState<Array<Types.EventPrizeStatsDto>>();
    const [displayEventPrizeStats, setDisplayEventPrizeStats] = useState<Array<Types.EventPrizeStatsDto>>();

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex, subDataIndex?: keyof Types.EventPrizeDto): ColumnType<Types.EventUserDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) => {
            let fieldData = record[dataIndex];
            if (subDataIndex !== undefined) {
                const subFieldData = fieldData as Types.EventPrizeDto;
                fieldData = subFieldData[subDataIndex] as string | number | boolean;
            }

            if (fieldData === null || fieldData === undefined) return false;

            return fieldData
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase());
        },
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.EventUserDto> = [
        {
            align: "center",
            title: "ID",
            dataIndex: "eventUserId",
            key: "eventUserId",
            ...getColumnSearchProps("eventUserId"),
        },
        {
            align: "center",
            title: "이름",
            dataIndex: "name",
            key: "name",
            ...getColumnSearchProps("name"),
        },
        {
            align: "center",
            title: "전화번호",
            dataIndex: "phone",
            key: "phone",
            ...getColumnSearchProps("phone"),
        },
        {
            align: "center",
            title: "상품명",
            dataIndex: ["eventPrize", "prizeName"],
            key: "prizeName",
            ...getColumnSearchProps("eventPrize", "prizeName"),
        },
        {
            align: "center",
            title: "수령",
            dataIndex: ["eventPrize", "received"],
            key: "received",
            render: (_, { eventPrize }) => {
                return eventPrize.received ? "수령" : "미수령";
            },
            filters: [
                {
                    text: "수령",
                    value: true,
                },
                {
                    text: "미수령",
                    value: false,
                },
            ],
            onFilter: (value: string | number | boolean, record) => {
                if (typeof value === "number" || typeof value === "string") return false;

                if (record.eventPrize.received === value) return true;
                else return false;
            },
        },
        {
            align: "center",
            title: "수령일자",
            dataIndex: ["eventPrize", "receivedDate"],
            key: "receivedDate",
            ...getColumnSearchProps("eventPrize", "receivedDate"),
            render: (_, { eventPrize }) => {
                if (eventPrize.receivedDate === null) return String.dash;
                return Utils.utcToLocalTime(eventPrize.receivedDate, Common.FORMAT_DATE_TIME);
            },
        },
        {
            align: "center",
            title: "참가날짜",
            dataIndex: "createdDate",
            key: "createdDate",
            ...getColumnSearchProps("createdDate"),
            render: (_, { createdDate }) => {
                return Utils.utcToLocalTime(createdDate, Common.FORMAT_DATE_TIME);
            },
        },

        {
            key: "detail",
            title: " ",
            dataIndex: "",
            render: (_, { phone }) => {
                return (
                    <Space size={10}>
                        <Button
                            shape="round"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                // requestReceivedPrize(phone);
                                confirmReceivedPrize(phone);
                            }}
                        >
                            수령변경
                        </Button>
                    </Space>
                );
            },
        },
    ];

    const columnsPrize: ColumnsType<Types.EventPrizeStatsDto> = [
        {
            align: "left",
            title: "상품명",
            dataIndex: ["prize", "prizeName"],
            key: "prizeName",
        },
        {
            align: "center",
            title: "참여",
            dataIndex: "selectedCount",
            key: "selectedCount",
            render: (_, { selectedCount }) => {
                return selectedCount ? selectedCount : String.dash;
            },
        },
        {
            align: "center",
            title: "수령",
            dataIndex: "receivedCount",
            key: "receivedCount",
            render: (_, { receivedCount }) => {
                return receivedCount ? receivedCount : String.dash;
            },
        },
        {
            align: "center",
            title: "잔여",
            dataIndex: "remainder",
            key: "remainder",
            render: (_, { remainder }) => {
                return remainder ? remainder : String.dash;
            },
        },
        {
            align: "center",
            title: "투입",
            dataIndex: "count",
            key: "count",
            render: (_, { count }) => {
                return count ? count : String.dash;
            },
        },
        {
            align: "center",
            title: "여분",
            dataIndex: "surplus",
            key: "surplus",
            render: (_, { surplus }) => {
                return surplus ? surplus : String.dash;
            },
        },
        {
            align: "center",
            title: "총수량",
            dataIndex: ["prize", "amount"],
            key: "amount",
            render: (_, { prize }) => {
                return prize.amount ? prize.amount : String.dash;
            },
        },
    ];

    const confirmReceivedPrize = (phone: string) => {
        Modal.confirm({
            title: "수령 변경",
            content: (
                <div>
                    상품 수령으로 변경하시겠습니까?
                    <br />
                    변경 후에 미수령으로 변경할 수 없습니다.
                </div>
            ),
            okText: "변경",
            onOk() {
                requestReceivedPrize(phone);
            },
            cancelText: "취소",
            onCancel() {},
            centered: true,
        });
    };

    useEffect(() => {
        setEventUsers([]);
        setDisplayEventPrizeStats([]);

        requestGetEventUsers();
        requestGetEventStats();
    }, [eventId]);

    const requestAxiosGetEventUsers = async () => {
        let response = await utilAxiosWithAuth().get(Request.EVENT_URL + eventId + Request.EVENT_USERS_URL);
        return response.data;
    };

    const {
        loading: loadingGetEventUsers,
        error: errorGetEventUsers,
        data: resultGetEventUsers,
        execute: requestGetEventUsers,
    } = useAsyncAxios(requestAxiosGetEventUsers);

    useEffect(() => {
        if (resultGetEventUsers === null) return;
        console.log("resultGetEventUsers", resultGetEventUsers);
        setEventUsers(resultGetEventUsers.eventUsers);
    }, [resultGetEventUsers]);

    useEffect(() => {
        if (errorGetEventUsers === null) return;

        console.log("errorGetEventUsers", errorGetEventUsers);
        alert.setAlert(errorGetEventUsers.response.data.code, "Error", errorGetEventUsers.response.data.message);
    }, [errorGetEventUsers]);

    const requestAxiosReceivedPrize = async (phoneNumber: string) => {
        let response = await utilAxiosWithAuth().post(Request.EVENT_URL + eventId + Request.EVENT_RECEIVED_PRIZES_URL, null, {
            params: { phone: phoneNumber },
        });
        return response.data;
    };

    const {
        loading: loadingReceivedPrize,
        error: errorReceivedPrize,
        data: resultReceivedPrize,
        execute: requestReceivedPrize,
    } = useAsyncAxios(requestAxiosReceivedPrize);

    useEffect(() => {
        if (resultReceivedPrize === null) return;
        console.log("resultReceivedPrize", resultReceivedPrize);
        requestGetEventUsers();
    }, [resultReceivedPrize]);

    useEffect(() => {
        if (errorReceivedPrize === null) return;

        console.log("errorReceivedPrize", errorReceivedPrize);
        alert.setAlert(errorReceivedPrize.response.data.code, "Error", errorReceivedPrize.response.data.message);
    }, [errorReceivedPrize]);

    const requestAxiosGetEventStats = async () => {
        let response = await utilAxiosWithAuth().get(Request.EVENT_URL + eventId + Request.EVENT_STATS_URL);
        return response.data;
    };

    const {
        loading: loadingGetEventStats,
        error: errorGetEventStats,
        data: resultGetEventStats,
        execute: requestGetEventStats,
    } = useAsyncAxios(requestAxiosGetEventStats);

    useEffect(() => {
        if (resultGetEventStats === null) return;
        console.log("resultGetEventStats", resultGetEventStats);
        // setEventStats(resultGetEventStats.eventStats);
        setEventPrizeStats(resultGetEventStats.eventStats);
    }, [resultGetEventStats]);

    useEffect(() => {
        if (errorGetEventStats === null) return;

        console.log("errorGetEventStats", errorGetEventStats);
        // alert.setAlert(errorGetEventStats.response.data.code, "Error", errorGetEventStats.response.data.message);
    }, [errorGetEventStats]);

    useEffect(() => {
        if (eventPrizeStats === undefined) return;

        const sumPrizeStats: Types.EventPrizeStatsDto = {
            prize: { prizeName: "합계", amount: 0 },
            count: 0,
            selectedCount: 0,
            receivedCount: 0,
            remainder: 0,
            surplus: 0,
        };

        eventPrizeStats.forEach((prizeStat) => {
            sumPrizeStats.prize.amount += prizeStat.prize.amount;
            sumPrizeStats.count += prizeStat.count;
            sumPrizeStats.selectedCount += prizeStat.selectedCount;
            sumPrizeStats.receivedCount += prizeStat.receivedCount;
            sumPrizeStats.remainder += prizeStat.remainder;
            sumPrizeStats.surplus += prizeStat.surplus;
        });

        setDisplayEventPrizeStats([sumPrizeStats, ...eventPrizeStats]);
    }, [eventPrizeStats]);

    return (
        <>
            <Col span={6}>
                <Divider orientation="left">
                    <span>참가자</span>
                </Divider>
            </Col>
            <Space direction="vertical" style={{ width: "100%" }}>
                <DataTable hasScroll={false} columns={columns} rowKey="eventUserId" dataSource={eventUsers} />
                <Collapse>
                    <Panel header="상품현황" key="prizes">
                        {/* <h3>전체 수량 : {eventStats?.totalCount}</h3>
                        <h3>잔여 수량 : {eventStats?.totalRemainder}</h3> */}

                        <DataTable
                            hasScroll={false}
                            columns={columnsPrize}
                            onRowKey={(record) => {
                                return record.prize.prizeName;
                            }}
                            dataSource={displayEventPrizeStats}
                        />
                    </Panel>
                </Collapse>
            </Space>
        </>
    );
}

export default EventUserList;
